import type { PayloadAction } from "@reduxjs/toolkit"

import type { ReduxMessage } from "@/common/types/chat"

import type { PlaygroundChatState } from "../playgroundTypes"

export const mergeMessagesWithOptimistic = (
  existingMessages: ReduxMessage[],
  newMessages: ReduxMessage[]
): ReduxMessage[] => {
  const existingById = new Map(existingMessages.map((m) => [m.id, m]))

  const merged = newMessages.map((newMsg) => {
    const existing = existingById.get(newMsg.id)
    if (existing && existing.updatedAt === newMsg.updatedAt) {
      return existing
    }
    return newMsg
  })

  const backendIds = new Set(newMessages.map((m) => m.id))
  const unconfirmed = existingMessages.filter((m) => !backendIds.has(m.id))

  if (unconfirmed.length > 0) {
    // Unconfirmed messages are always the most recent (just sent by the user),
    // so append after backend-ordered messages. No timestamp sort needed —
    // backend order is authoritative, and optimistic messages belong at the end.
    merged.push(...unconfirmed)
  }

  // If no unconfirmed optimistic messages and all items are the same references
  // in the same order, return the original array to preserve referential equality
  if (
    unconfirmed.length === 0 &&
    merged.length === existingMessages.length &&
    merged.every((msg, i) => msg === existingMessages[i])
  ) {
    return existingMessages
  }

  return merged
}

/**
 * Add a single message to chat
 */
export const addMessage = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; message: ReduxMessage }>
) => {
  const { chatId, message } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  const existingIndex = chat.messages.findIndex((m) => m.id === message.id)

  if (existingIndex >= 0) {
    chat.messages[existingIndex] = message
  } else {
    chat.messages.push(message)
  }

  if (message.type === "AI") {
    chat.shouldShowDynamicWhitespace = false
  }
}

/**
 * Set message history with smart diffing
 * Only updates if data actually changed - prevents unnecessary re-renders
 */
export const setMessages = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; messages: ReduxMessage[]; hasMore?: boolean }>
) => {
  const { chatId, messages, hasMore } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  const existingMessages = chat.messages || []

  const merged = mergeMessagesWithOptimistic(existingMessages, messages)
  if (merged === existingMessages) {
    // No change — skip update entirely
    if (hasMore !== undefined) {
      chat.hasMoreHistory = hasMore
    }
    return
  }

  chat.messages = merged
  chat.messagesLoaded = true

  if (hasMore !== undefined) {
    chat.hasMoreHistory = hasMore
  }
}

/**
 * Add a single message from WebSocket (for AI responses)
 * With robust deduplication to prevent duplicate messages
 */
export const addMessageFromSocket = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; message: ReduxMessage }>
) => {
  const { chatId, message } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // First check: exact ID match
  const existingIndex = chat.messages.findIndex((m) => m.id === message.id)

  if (existingIndex >= 0) {
    // Update existing message (e.g., optimistic message being confirmed)
    chat.messages[existingIndex] = message
    return
  }

  // Second check: prevent duplicate HUMAN messages (safety net for race conditions)
  // Check if a message with same content and type exists within 2 seconds
  if (message.type === "HUMAN") {
    const isDuplicateHuman = chat.messages.some((m) => {
      if (m.type !== "HUMAN") return false

      const isSameContent = m.textContent === message.textContent
      const mCreatedAt = m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt)
      const msgCreatedAt =
        message.createdAt instanceof Date ? message.createdAt : new Date(message.createdAt)
      const isRecentlyCreated = Math.abs(mCreatedAt.getTime() - msgCreatedAt.getTime()) < 2000

      return isSameContent && isRecentlyCreated
    })

    if (isDuplicateHuman) {
      return
    }
  }

  // No duplicate found - add new message
  chat.messages.push(message)
}

/**
 * Update chat topic
 */
export const updateChatTopic = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; topic: string }>
) => {
  const { chatId, topic } = action.payload
  const chat = state.chats[chatId]

  if (chat) {
    chat.topic = topic
  }
}
